import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { fetchCreateContactEmail } from 'src/redux/contact'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'
import { useIdentity } from 'pages/Identity/Login'

const UPGRADE_SUBJECT = 'Upgrade Request - Full Hospital Access'

const UPGRADE_BENEFITS = [
  'All Professional plan features, plus:',
  'Unlimited users for your whole team at a lower per-user cost',
  'Connect directly with qualified, credentialed healthcare professionals',
  'AI candidate matching across all of your jobs',
  'Hire directly with no recruiter markups or staffing agency fees',
]

const UpgradeRequestDialog = ({ open, onClose }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { email } = useIdentity()
  const employerUser = useSelector(state => state.employerUser)
  const sending = useSelector(state => state.contact.loading)
  const [message, setMessage] = useState('')

  const handleSubmit = () => {
    const requestDetails = [
      `Name: ${employerUser.firstName} ${employerUser.lastName}`,
      `Email: ${email}`,
      `Organization: ${employerUser.employerName || 'Unknown'}`,
      'Requested plan: Health Care System (full hospital access)',
    ].join('\n')

    const values = {
      firstName: employerUser.firstName,
      lastName: employerUser.lastName,
      email,
      subject: UPGRADE_SUBJECT,
      message: message ? `${requestDetails}\n\n${message}` : requestDetails,
    }

    dispatch(
      fetchCreateContactEmail({
        values,
        onSuccess: () => {
          dispatch(
            fetchTrackEvent({
              eventName: 'enterprise_upgrade_requested',
              eventType: ProductAnalyticsEventTypes.ACTION,
              distinctId: email,
            }),
          )
          setMessage('')
          onClose()
        },
      }),
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant='h6' fontWeight={700}>
          Upgrade to Full Hospital Access
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Health Care System plans give your whole organization access at a lower per-user cost:
        </Typography>

        <Box sx={{ mb: 3 }}>
          {UPGRADE_BENEFITS.map(benefit => (
            <Box
              key={benefit}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main, mt: '2px' }} />
              <Typography variant='body2'>{benefit}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '8px',
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            We&apos;ll reach out to
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {employerUser.firstName} {employerUser.lastName}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {email}
          </Typography>
          {employerUser.employerName && (
            <Typography variant='body2' color='text.secondary'>
              {employerUser.employerName}
            </Typography>
          )}
        </Box>

        <TextField
          label='Anything we should know? (optional)'
          placeholder='Team size, timeline, questions...'
          value={message}
          onChange={event => setMessage(event.target.value)}
          multiline
          rows={3}
          fullWidth
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={sending}
          startIcon={sending ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {sending ? 'Sending...' : 'Request Upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpgradeRequestDialog
